import config from 'shared/config';
import connector from 'api/http';

import { makeApiError, makeApiSuccess } from 'scenes/common/helpers/apiData';
import { VehicleManuallyEntered } from 'scenes/motor/types';
import { ApiData } from 'scenes/common/types';
import { Make } from './types';

const api = (baseUrl: string = config.baseUrls.BFF_BASE_URL) => {
  const server = connector(baseUrl);

  return {
    getMakes: (): Promise<ApiData<Make[]>> =>
      server
        .get('/vehicle/make')
        .then(({ vehicles }) =>
          vehicles?.length > 0
            ? makeApiSuccess(
                vehicles.sort((a: Make, b: Make) =>
                  a.make.localeCompare(b.make)
                )
              )
            : makeApiError<Make[]>('Not found')
        )
        .catch((err) => makeApiError<Make[]>(err.message)),

    getModels: (make: string): Promise<ApiData<string[]>> =>
      server
        .get('/vehicle', { make })
        .then(({ vehicles }) =>
          vehicles?.length > 0
            ? makeApiSuccess(
                vehicles
                  .map((vehicle: VehicleManuallyEntered) => vehicle.model)
                  .sort((a: string, b: string) => a.localeCompare(b))
              )
            : makeApiError<string[]>('Not found')
        )
        .catch((err) => makeApiError<string[]>(err.message)),

    getYears: (make: string, model: string): Promise<ApiData<string[]>> =>
      server
        .get('/vehicle', { make, model })
        .then(({ vehicles }) =>
          vehicles?.length > 0
            ? makeApiSuccess(
                vehicles
                  .map((vehicle: VehicleManuallyEntered) => vehicle.year)
                  .sort((a: number, b: number) => b - a)
              )
            : makeApiError<string[]>('Not found')
        )
        .catch((err) => makeApiError<string[]>(err.message)),

    getTypes: (
      make: string,
      model: string,
      year: string
    ): Promise<ApiData<string[]>> =>
      server
        .get('/vehicle', { make, model, year })
        .then(({ vehicles }) =>
          vehicles?.length > 0
            ? makeApiSuccess(
                vehicles
                  .map((vehicle: VehicleManuallyEntered) => vehicle.bodyType)
                  .sort((a: string, b: string) => a.localeCompare(b))
              )
            : makeApiError<string[]>('Not found')
        )
        .catch((err) => makeApiError<string[]>(err.message)),

    getEngineTypes: (
      make: string,
      model: string,
      year: string,
      type: string
    ): Promise<ApiData<string[]>> =>
      server
        .get('/vehicle', { make, model, year, type })
        .then(({ vehicles }) =>
          vehicles?.length > 0
            ? makeApiSuccess(
                vehicles
                  .map(
                    (vehicle: VehicleManuallyEntered) =>
                      vehicle.engine.description
                  )
                  .sort((a: string, b: string) => a.localeCompare(b))
              )
            : makeApiError<string[]>('Not found')
        )
        .catch((err) => makeApiError<string[]>(err.message)),

    getVehicle: (
      make: string,
      model: string,
      year: string,
      type: string,
      engineType: string
    ): Promise<ApiData<VehicleManuallyEntered>> =>
      server
        .get('/vehicle', { make, model, year, type })
        .then(({ vehicles }) => {
          const resultVehicle = vehicles.find(
            (vehicle: VehicleManuallyEntered) =>
              vehicle.engine.description.includes(engineType)
          );
          if (resultVehicle) {
            return makeApiSuccess(resultVehicle);
          }
          return makeApiError<VehicleManuallyEntered>('Not found');
        })
        .catch((err) => makeApiError<VehicleManuallyEntered>(err.message)),
  };
};

export default api;
